// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  setCartOwner,
  selectCartProductOwner,
  selectCartReselleeID,
  selectHasOwnerOnly,
  selectCanChooseResellee,
} from '@gandi/cart';
import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Button } from '@gandi/ui';

import { logging } from 'gandi.redux-logger';

import { CUSTOMER } from 'src/Cart/AccountChoice/components/ResellerOwnerType';
import {
  ensureCorporatePack,
  updateProductsPayload,
} from 'src/Cart/AccountChoice/modules/actions';
import { mustCartOwnerBeReseller } from 'src/Cart/AccountChoice/modules/helpers';
import {
  selectCanChooseProductOwner,
  selectCartOwners,
  selectCurrentOrganizationCanPay,
} from 'src/Cart/AccountChoice/modules/selectors';
import { selectHasDomainTransfer } from 'src/Cart/Setup/modules/selectors';
import { useAppSelector } from 'src/hooks/useAppSelector';
import { applyTemplateIfNeeded } from 'src/modules/app/actions';
import {
  selectShopCurrent,
  selectHasMissingOrgInfo,
} from 'src/modules/organization/selectors';

import { useCartSummaryContext } from '../../../context/CartSummary';

import { ButtonContent } from './ButtonContent';

import styles from './Button.scss';

type Params = {
  cartId: string,
};

type Props = {
  price: string,
};

export function ContinueButton({ price }: Props): React.MixedElement | null {
  const dispatch = useDispatch();
  const translator = useTranslator();

  const {
    frozen,
    handleConfigurationClick,
    setFrozen,
    ownerType,
    submitting,
    setSubmitting,
    updateSharingIdContext,
  } = useCartSummaryContext();

  const { __ } = translator;
  const { cartId } = useParams<Params>();

  const logger = logging(dispatch, 'AccountChoice');

  const [continueTo, setContinueTo] = React.useState<?string>();

  const { id: sharingId, reseller } = useAppSelector(state =>
    selectShopCurrent(state, true),
  );

  const productOwner = useAppSelector(selectCartProductOwner);
  const missingCartOwnerInfo = useAppSelector(state => {
    return !!selectHasMissingOrgInfo(state, sharingId) && sharingId;
  });

  const missingProductOwnerInfo = useAppSelector(state => {
    return !!selectHasMissingOrgInfo(state, productOwner) && productOwner;
  });

  const canPay = useAppSelector(selectCurrentOrganizationCanPay);
  const resellee = useAppSelector(selectCartReselleeID) || undefined;
  const cartOwners = useAppSelector(selectCartOwners);

  const hasDomainTransfer = useAppSelector(selectHasDomainTransfer);

  const ownerOnly = useAppSelector(selectHasOwnerOnly);
  // $FlowFixMe PartialOrganization should be compatible with CartOwnerCandidate
  const ownerMustReseller = mustCartOwnerBeReseller(ownerOnly, cartOwners);

  const chooseOwner = useAppSelector(selectCanChooseProductOwner);
  const chooseResellee = useAppSelector(state => {
    return reseller && selectCanChooseResellee(state);
  });

  const missingInfo = missingCartOwnerInfo || missingProductOwnerInfo;

  const driveUserToSetup = !missingInfo && hasDomainTransfer;

  React.useEffect(() => {
    setFrozen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (missingProductOwnerInfo !== false) {
      setContinueTo(
        `/cart/${cartId}/configure/account/${missingProductOwnerInfo}?owner=products`,
      );
    }
    if (missingCartOwnerInfo !== false) {
      setContinueTo(
        `/cart/${cartId}/configure/account/${missingCartOwnerInfo}?owner=cart`,
      );
    }
  }, [missingProductOwnerInfo, missingCartOwnerInfo, cartId]);

  const disabled =
    frozen ||
    !canPay ||
    (ownerMustReseller && !reseller) ||
    (!!(chooseResellee && !chooseOwner && reseller) && !resellee) ||
    !!(reseller && ownerType === CUSTOMER && !resellee) ||
    submitting;

  async function handleContinueClick(evt: SyntheticEvent<>): Promise<void> {
    evt.preventDefault();

    setFrozen(true);
    setSubmitting(true);

    try {
      await dispatch(applyTemplateIfNeeded());
      await Promise.allSettled([
        dispatch(ensureCorporatePack(translator)),
        dispatch(updateProductsPayload({ translator })),
      ]);
      await dispatch(setCartOwner({}));
      setFrozen(true);
      setSubmitting(false);

      // Perform a hard-reload only if the cart owner / sharing_id context
      // has been updated.
      const reload = !global.window.location.href.includes(sharingId);
      await updateSharingIdContext({
        newSharingId: sharingId,
        guess: false,
        route: `/cart/${cartId}/overview`,
        reload,
      });
    } catch (err) {
      setFrozen(false);
      setSubmitting(false);
      dispatch(notifyDanger(__('Unexpected error occurred')));
      logger.error({ err }, 'Unable to update products payload.');
    }
  }

  function handleClick(e: SyntheticEvent<>) {
    if (!disabled && !missingInfo) {
      if (hasDomainTransfer) {
        handleConfigurationClick(e);
      } else {
        handleContinueClick(e);
      }
    }
  }

  if (continueTo) {
    return (
      <Button
        className={styles.button}
        block
        loading={submitting}
        disabled={disabled}
        to={continueTo}
        onClick={handleClick}
      >
        <ButtonContent addon={price}>{__('Continue')}</ButtonContent>
      </Button>
    );
  }

  return (
    <Button
      className={styles.button}
      block
      loading={submitting}
      disabled={disabled}
      onClick={handleClick}
    >
      <ButtonContent addon={price}>
        {driveUserToSetup
          ? __('Continue')
          : __(
              '<for-translators-use>Translate to "Continue"</for-translators-use>Checkout',
            )}
      </ButtonContent>
    </Button>
  );
}
